// ============================================================================
// Practice Intros
// ----------------------------------------------------------------------------
// Short, plain-language description of WHO each practice is for. Shown as a
// floating "is this a good fit?" card before the user begins task 1 so they
// can confidently opt in or back out without committing to a session.
// ============================================================================

export const PRACTICE_INTROS = {
  quiet_the_worry: {
    headline: "How this practice helps:",
    body: "This practice is for moments when your mind feels stuck in a loop of worry — replaying the same fear, what-if, or anxious thought you can't put down. This guided bilateral practice gently nudges your nervous system out of the loop so the worry loses its grip.",
  },
  shake_off_sadness: {
    headline: "How this practice helps:",
    body: "This practice is for heavy, blue feelings that have been hanging over you — a low mood that won't lift, a tightness in your chest, a numbness that's hard to shake. This guided bilateral practice helps your brain begin to process and release the feeling so you can breathe a little easier.",
  },
  calm_your_anger: {
    headline: "How this practice helps:",
    body: "This practice is for moments when anger or irritation is bigger than the situation called for — flashes of heat, a clenched jaw, a racing pulse. This guided bilateral practice will help your body discharge the overlearned activation so you can respond from a calmer place instead of reacting from agitation — now and in the future.",
  },
  relief_from_grief: {
    headline: "How this practice helps:",
    body: "This practice is for the weight of loss — a person, a relationship, a pet, a future you'd expected. Loss is hard, but finding peace with it doesn't have to be. This guided bilateral practice will soften the sharpest edges and help deepen peace inside you so you have space to move forward with steadiness and calm.",
  },
  overcome_overwhelm: {
    headline: "How this practice helps:",
    body: "This practice is for when too much is happening at once — your to-do list, your inbox, your week — and your body feels frozen or scattered. You may feel easily overwhelmed much of the time. This guided bilateral practice helps your nervous system come back online so you can think clearly and take the next single step without the pile crushing you.",
  },
  dial_down_tension: {
    headline: "How this practice helps:",
    body: "This practice is for body tension that's been building all day... perhaps for months or years — tight shoulders, a clenched jaw, a knot between your shoulder blades, an inability to relax. This guided bilateral practice, paired with body awareness, gently unwinds the holding patterns so the body can finally release and learn to stay released.",
  },
  headache_relief: {
    headline: "How this practice helps:",
    body: "This practice is for tension-pattern headaches — the kind that builds across the back of your head, behind your eyes, or along your jaw and neck. This guided bilateral practice softens the surrounding tension so the headache can begin to ease. (Not a substitute for medical care if the headache is severe, new, or unusual.)",
  },
  fear_of_sleeplessness: {
    headline: "How this practice helps:",
    body: "This practice is for the anxious loop that shows up at bedtime — worry about whether you'll fall asleep, frustration at being awake, racing thoughts when the room goes quiet. This guided bilateral practice quiets your nervous system so the body can drop into rest.",
  },
  nightmare_reset: {
    headline: "How this practice helps:",
    body: "This practice is for the after-effects of a nightmare — the lingering fear, the held breath, the dread of going back to sleep. This guided bilateral practice helps your brain complete the REM-processing that the nightmare got stuck on, so the nightmare loses its grip on your nights.",
  },
  boost_self_worth: {
    headline: "How this practice helps:",
    body: "This practice is for the small voice that says you're not enough, not lovable, not worthy. This guided bilateral practice helps you neutralize negative self-talk with positive statements that land firmly and stick in your body — not just your head — so they begin to feel true.",
  },
  hold_boundaries: {
    headline: "How this practice helps:",
    body: "This practice is for the conversations where you know what you need to say but freeze, soften, or back down. This guided bilateral practice helps you hold steady so you can speak from a calmer, more grounded place — and stay there.",
  },
  beat_stage_fright: {
    headline: "How this practice helps:",
    body: "This practice is for the body-level dread before a presentation, audition, performance, or any moment in front of an audience. This guided bilateral practice helps your nervous system stop reading the moment as a threat so you can show up clear, present, and yourself.",
  },
  enhance_performance: {
    headline: "How this practice helps:",
    body: "This practice is for athletes, performers, presenters, and anyone preparing for a high-stakes moment. This guided bilateral practice, paired with mental rehearsal, helps your body and mind sync up around the version of you that's calm, confident, and ready.",
  },
  healing_old_stories: {
    headline: "How this practice helps:",
    body: "This practice is for the old wounds that still tug at you — the moments from years ago that still sting, the stories you've told yourself for so long they feel like truth. This guided bilateral practice helps your brain reprocess the experience so it stops hurting at the same intensity.",
  },
  new_habit_new_you: {
    headline: "How this practice helps:",
    body: "This practice is for the habit you keep meaning to start — or stop — and can't seem to make stick. This guided bilateral practice, paired with intention-setting, helps the new pattern feel familiar to your nervous system so it becomes easier to choose.",
  },
  fear_of_flying: {
    headline: "How this practice helps:",
    body: "This guided bilateral practice uses gentle left-right activity — the same kind your brain does naturally during deep REM sleep — to help you process and settle whatever fears of air travel you carry.",
  },
  cpap_claustrophobia: {
    headline: "How this practice helps:",
    body: "This guided bilateral practice uses gentle left-right activity — the same kind your brain does naturally during deep REM sleep — so the mask becomes effortless and you have the rest you deserve.",
  },
  fear_of_small_places: {
    headline: "How this practice helps:",
    body: "This guided bilateral practice uses gentle left-right activity — the same kind your brain does naturally during deep REM sleep — so small or tight places like MRIs, elevators, and crowded rooms become manageable.",
  },
  new_experience_anxiety: {
    headline: "How this practice helps:",
    body: "This guided bilateral practice uses gentle left-right activity — the same kind your brain does naturally during deep REM sleep — so you can do a new thing as yourself... without the worry or fear bullying you.",
  },
  resist_an_urge: {
    headline: "How this practice helps:",
    body: "This guided bilateral practice uses gentle left-right activity — the same kind your brain does naturally during deep REM sleep — so the choice to skip it becomes easier and more natural instead of forced.",
  },
};

// Fallback for any future intervention type that doesn't have its own intro.
export const DEFAULT_PRACTICE_INTRO = {
  headline: "How this practice helps:",
  body: "This guided bilateral practice uses gentle left-right activity — the same kind your brain does naturally during deep REM sleep — to help you process and settle whatever you're carrying right now.",
};

// Time expectation appended to every intro so members know what to plan
// for and understand they can pause and resume any time. Rendered by
// the "How this practice helps" modal (InterventionFlow) and the
// marketing detail page.
export const PRACTICE_TIME_NOTE =
  "Give yourself about 45 minutes to complete this practice. Don't have the time? You can always start and come back later!";

export const getPracticeIntro = (type) =>
  PRACTICE_INTROS[type] || DEFAULT_PRACTICE_INTRO;
